import { Button, Flex, BoxProps } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useUserData } from "../../hooks/useUserData";
import {
  ContentSectionCustomCard,
  Section,
  ReadStatus,
  Release,
  Bundle,
  ReadingList,
} from "../../types";
import { useMobileBreakpoint } from "../../utils/useBreakpoints";
import {
  FunnelConfigTypes,
  FunnelContextArea,
  FunnelOnSuccessResult,
  EventContextArea,
} from "../funnel/types";
import { FUNNEL_TYPES } from "../funnel/utils/contants";
import { ReadingStatusButton } from "../readingStatusButton/ReadingStatusButton";
import { useSlideInMenu } from "../slideInMenu/hooks/useSlideInMenu";
import { CONTEXT_ENTITY_TYPES } from "./constants";
import { ReleaseCardMenu } from "./ReleaseCardMenu";
import { setFunnelModalConfig } from "../funnel/FunnelModal.tsx";
import { doesUserHaveAccess } from "../../utils/doesUserHaveAccess.ts";
import { ReleasePricingActions } from "./ReleasePricingActions";
import { useReleasePricingInfo } from "../../hooks/useReleasePricingInfo.ts";
import { trackRecsysCardItemClick } from "../../analytics/recsys/tracking";
import { TFunction } from "i18next";
import { ReaderNavigationContext } from "../../screens/reader/types";
import { applyNavigationAnalyticsAttribution } from "../../analytics/params/navigationContextAttribution";
import { ClaimWithGoldButton } from "./ClaimWithGoldButton";
import { markPendingGoldReleaseAutoPurchase } from "../payments/utils/pendingGoldReleaseAutoPurchase";

interface FeatureCardALCButtonsProps extends BoxProps {
  contentCard: ContentSectionCustomCard;
  isHidden?: boolean;
  isRoadblock?: boolean;
  index?: number;
  sectionIndex?: number;
  section?: Section;
  buttonWidth?: string | Record<string, string>;
  goToCTAUrl: () => void;
  goToCTAUrlWithContext: (overrides?: Partial<ReaderNavigationContext>) => void;
  navigationContext?: ReaderNavigationContext;
}

export function FeatureCardALCButtons({
  contentCard,
  isHidden = false,
  isRoadblock = false,
  goToCTAUrl,
  index,
  sectionIndex,
  section,
  buttonWidth,
  navigationContext,
  goToCTAUrlWithContext,
  ...props
}: FeatureCardALCButtonsProps) {
  const isMobile = useMobileBreakpoint();
  const isComicCard =
    contentCard.context_entity_type_id === CONTEXT_ENTITY_TYPES.comic;
  const isListCard =
    contentCard.context_entity_type_id === CONTEXT_ENTITY_TYPES.list;

  const { userData } = useUserData();
  const userIsGold = userData?.gold ?? false;
  const { openReleaseInfoMenu, openBundleDetails } = useSlideInMenu();

  const releaseData =
    !isComicCard && !isListCard && contentCard.release
      ? contentCard.release
      : undefined;
  const comicDataToUse =
    isComicCard && contentCard.comic
      ? contentCard.comic
      : contentCard.release?.comic;
  const bundleData = contentCard.bundle;
  const listData = isListCard ? contentCard.list : null;

  const handleSubscriptionSuccess = (result: FunnelOnSuccessResult) => {
    if (
      result.context?.purchaseRelease &&
      result.options?.autoPurchaseReleaseWithSavedPaymentMethod
    ) {
      markPendingGoldReleaseAutoPurchase(result.context.purchaseRelease.id);
      setFunnelModalConfig({
        type: FUNNEL_TYPES.purchaseRelease,
        context: result.context,
        options: {
          ...result.options,
          onSuccess: () => setFunnelModalConfig(),
        },
      });
      return;
    }

    setFunnelModalConfig({
      type: FUNNEL_TYPES.congrats,
      context: result.context,
      options: {
        ...result.options,
        onSuccess: () => setFunnelModalConfig(),
      },
    });
  };

  const handleFunnelSuccess = (
    result: FunnelOnSuccessResult,
    metadata?: {
      nextType: FUNNEL_TYPES;
    },
  ) => {
    const release = result.data?.[1]?.data?.payload?.results;
    const { userHasAccess } = doesUserHaveAccess(result.data);
    const { nextType } = metadata || {};
    if (userHasAccess) {
      setFunnelModalConfig({
        type:
          nextType === FUNNEL_TYPES.purchaseRelease
            ? FUNNEL_TYPES.purchaseRelease
            : FUNNEL_TYPES.goldAccess,
        context: {
          ...result.context,
          purchaseRelease: release as Release,
        },
        options: {
          ...result.options,
          onSuccess: () => setFunnelModalConfig(),
        },
      });
    } else {
      setFunnelModalConfig({
        type: nextType,
        context: result.context,
        options: {
          ...result.options,
          autoPurchaseReleaseWithSavedPaymentMethod:
            nextType === FUNNEL_TYPES.subscription &&
            !!result.context?.purchaseRelease,
          onSuccess:
            nextType === FUNNEL_TYPES.subscription
              ? (result) => handleSubscriptionSuccess(result)
              : () => setFunnelModalConfig(),
        },
      });
    }
  };

  const handlePurchaseClick = (config: FunnelConfigTypes) => {
    const context = {
      ...config.context,
      modalContext: FunnelContextArea.featureCard,
    };

    if (userData) {
      if (context.purchaseRelease) {
        markPendingGoldReleaseAutoPurchase(context.purchaseRelease.id);
      }
      setFunnelModalConfig({
        type: FUNNEL_TYPES.purchaseRelease,
        context,
        options: {
          ...config.options,
          onSuccess: () => setFunnelModalConfig(),
        },
      });
    } else {
      setFunnelModalConfig({
        type: FUNNEL_TYPES.signup,
        context,
        options: {
          skipOnboarding: true,
          onSuccess: (result) => {
            handleFunnelSuccess(result, {
              nextType: FUNNEL_TYPES.purchaseRelease,
            });
          },
        },
      });
    }
  };

  const handleGoldPaywallClick = (config: FunnelConfigTypes) => {
    const context = {
      ...config.context,
      modalContext: FunnelContextArea.featureCard,
    };
    if (userData) {
      setFunnelModalConfig({
        type: FUNNEL_TYPES.subscription,
        context,
        options: {
          ...config.options,
          autoPurchaseReleaseWithSavedPaymentMethod:
            config.options?.autoPurchaseReleaseWithSavedPaymentMethod ||
            !!context.purchaseRelease,
          onSuccess: handleSubscriptionSuccess,
        },
      });
    } else {
      setFunnelModalConfig({
        type: FUNNEL_TYPES.signup,
        context,
        options: {
          skipOnboarding: true,
          onSuccess: (result) => {
            handleFunnelSuccess(result, {
              nextType: FUNNEL_TYPES.subscription,
            });
          },
        },
      });
    }
  };

  const handleButtonClick = (config?: FunnelConfigTypes) => {
    const impressionId =
      section && sectionIndex !== undefined
        ? trackRecsysCardItemClick({
            section,
            sectionIndex,
            item: contentCard,
            itemIndex: index ?? 0,
          })
        : undefined;

    if (config) {
      if (config.type === FUNNEL_TYPES.purchaseRelease) {
        handlePurchaseClick(config);
        return;
      } else if (config.type === FUNNEL_TYPES.subscription) {
        handleGoldPaywallClick(config);
        return;
      } else if (config.type === FUNNEL_TYPES.purchasePod) {
        setFunnelModalConfig({
          type: FUNNEL_TYPES.purchasePod,
          context: {
            ...config.context,
            modalContext: FunnelContextArea.featureCard,
          },
          options: {
            onSuccess: () => setFunnelModalConfig(),
          },
        });
        return;
      }
    }

    if (impressionId) {
      goToCTAUrlWithContext({ impressionId });
    } else {
      goToCTAUrl();
    }
  };

  const handleReleaseDetails = () => {
    const impressionId =
      section && sectionIndex !== undefined
        ? trackRecsysCardItemClick({
            section,
            sectionIndex,
            item: contentCard,
            itemIndex: index ?? 0,
          })
        : undefined;

    if (!releaseData?.key) return;
    openReleaseInfoMenu(
      releaseData.key,
      releaseData.comic?.slug ?? releaseData.comic_slug,
      applyNavigationAnalyticsAttribution(navigationContext, {
        impressionId,
      }),
    );
  };

  const { isUnlocked, isPremiumOnly, pricePointToUse } =
    useReleasePricingInfo(releaseData);
  const { user_read_status } = releaseData ?? {};

  const isALCRelease = !isComicCard && (isPremiumOnly || pricePointToUse);

  return (
    <Flex
      alignItems={releaseData || bundleData ? "end" : "center"}
      width="100%"
      mt="16px"
      gap="8px"
      justifyContent={!(isRoadblock && !isMobile) ? "space-between" : undefined}
      {...props}
    >
      {releaseData ? (
        <ReleasePricingActions
          contextArea={FunnelContextArea.featureCard}
          eventContextArea={EventContextArea.featureCard}
          release={releaseData}
          setFunnelConfig={handleButtonClick}
          hideFreeWithGold
          primaryButtonMaxWidth={buttonWidth}
          buttonSize="sm"
          escapeContainerBounds
        />
      ) : listData ? (
        <ReadingListButton
          list={listData}
          buttonWidth={buttonWidth}
          onTrackClick={() => {
            if (section && sectionIndex !== undefined) {
              trackRecsysCardItemClick({
                section,
                sectionIndex,
                item: contentCard,
                itemIndex: index ?? 0,
              });
            }
          }}
        />
      ) : bundleData ? (
        <BundleButton
          bundle={bundleData}
          buttonWidth={buttonWidth}
          openBundleDetails={openBundleDetails}
          userIsGold={userIsGold}
          onTrackClick={() => {
            if (section && sectionIndex !== undefined) {
              trackRecsysCardItemClick({
                section,
                sectionIndex,
                item: contentCard,
                itemIndex: index ?? 0,
              });
            }
          }}
        />
      ) : (
        <StartContinueReadingButton
          userReadStatus={comicDataToUse?.user_read_status}
          isHidden={isHidden}
          handleButtonClick={handleButtonClick}
          buttonWidth={buttonWidth}
          isRoadblock={isRoadblock}
          isMobile={isMobile}
        />
      )}
      {isComicCard ? (
        <ReadingStatusButton
          cacheKey={`${comicDataToUse?.entity_type}-${comicDataToUse?.id}`}
          isSmall
          width="24px"
          useBookmark
          navigationContext={navigationContext}
        />
      ) : (
        releaseData && (
          <ReleaseCardMenu
            backgroundColor="transparent.white.10"
            margin="0 0 4px 0px"
            menuPlacement="top-end"
            iconSize="24px"
            onPreviewRelease={
              isUnlocked || (userIsGold && !isALCRelease)
                ? undefined
                : goToCTAUrl
            }
            shareUrl={releaseData.share_url}
            onStartReading={
              isUnlocked && !user_read_status ? goToCTAUrl : undefined
            }
            onContinueReading={
              isUnlocked && user_read_status ? goToCTAUrl : undefined
            }
            onReleaseDetails={handleReleaseDetails}
          />
        )
      )}
    </Flex>
  );
}

function getReadingStatusText(
  userReadStatus: ReadStatus | null | undefined,
  t: TFunction,
): string {
  if (userReadStatus) {
    return t("components.cards.customCard.buttonText.continueReading");
  } else {
    return t("components.cards.customCard.buttonText.startReading");
  }
}

interface StartContinueReadingButtonProps {
  userReadStatus: ReadStatus | null | undefined;
  isHidden: boolean;
  handleButtonClick: () => void;
  buttonWidth?: string | Record<string, string>;
  isRoadblock: boolean;
  isMobile: boolean;
}

function StartContinueReadingButton({
  userReadStatus,
  isHidden,
  handleButtonClick,
  buttonWidth,
  isRoadblock,
  isMobile,
}: StartContinueReadingButtonProps) {
  const { t } = useTranslation();

  return (
    <Button
      onClick={!isHidden ? handleButtonClick : undefined}
      variant="primary"
      size="md"
      width={
        buttonWidth ||
        (!(isRoadblock && !isMobile)
          ? "full"
          : { base: "200px", sm: "278px", md: "350px" })
      }
    >
      {getReadingStatusText(userReadStatus, t)}
    </Button>
  );
}

interface BundleButtonProps {
  bundle: Bundle;
  buttonWidth?: string | Record<string, string>;
  openBundleDetails: (bundleUuid: string) => void;
  onTrackClick?: () => void;
  userIsGold?: boolean;
}

function BundleButton({
  bundle,
  buttonWidth,
  openBundleDetails,
  onTrackClick,
  userIsGold = false,
}: BundleButtonProps) {
  const { t } = useTranslation();

  // Claimable Gold-monthly bundle — render the shared ClaimWithGoldButton
  // (routes to /my-shelf/gold-rewards) instead of the Buy/See-details flow.
  // Same UX as the release branch.
  const isClaimable =
    userIsGold &&
    bundle.gold_monthly_title_id &&
    bundle.gold_monthly_claim_status === "unclaimed";

  if (isClaimable) {
    return (
      <ClaimWithGoldButton
        size="md"
        width={buttonWidth || "full"}
        onBeforeNavigate={onTrackClick}
      />
    );
  }

  const handleClick = () => {
    onTrackClick?.();

    if (bundle.uuid) {
      openBundleDetails(bundle.uuid);
    }
  };

  const buttonText = bundle.user_owns_all_items
    ? t("components.cards.customCard.buttonText.seeDetails")
    : t("components.cards.customCard.buttonText.buyNow");

  return (
    <Button
      onClick={handleClick}
      variant="primary"
      size="md"
      width={buttonWidth || "full"}
    >
      {buttonText}
    </Button>
  );
}

interface ReadingListButtonProps {
  list: ReadingList;
  buttonWidth?: string | Record<string, string>;
  onTrackClick?: () => void;
}

function ReadingListButton({
  list,
  buttonWidth,
  onTrackClick,
}: ReadingListButtonProps) {
  const { t } = useTranslation();

  const handleClick = () => {
    onTrackClick?.();

    if (list.url) {
      window.location.href = list.url;
    }
  };

  return (
    <Button
      onClick={handleClick}
      variant="primary"
      size="md"
      width={buttonWidth || "full"}
      isDisabled={!list.url}
    >
      {t("components.cards.customCard.buttonText.viewList")}
    </Button>
  );
}
